import {mix} from "@spa/utils/assetPathResolver";
import {memo} from "react";
import {useFocus} from "@spa/features/search_job/pc/contexts/FocusContext";
import {usePage} from "@inertiajs/react";

const JobPanel = ({
    position,
    jobViewModel,
    isSaved,
    handleSaveJob,
    isActive,
    isFocus,
    onPanelClick,
    onPanelKeyPress,
    onPanelFocus,
    onPanelBlur,
    translationsJobItems,
}) => {
    const { translations } = usePage().props;
    const {focusPanelRefs} = useFocus();

    return (
        <li
            id="s-jobPanel"
            className={`p-job p-job--search s-jobPanelForcus ${isActive ? 'is-active' : ''} ${isFocus ? 'is-focus' : ''}`}
            onClick={() => {onPanelClick(jobViewModel, position)}}
            onKeyPress={(e) => {onPanelKeyPress(e, jobViewModel)}}
        >
            <div className="p-job_cont">
                <h2 className="p-job_title p-job_title--search">
                    <button
                        className="p-job_title_btn s-jobPanelBtn"
                        aria-pressed={isActive}
                        aria-label={translationsJobItems.open_the_job_details.replace(':title', jobViewModel.title)}
                        onFocus={() => onPanelFocus(jobViewModel)}
                        onBlur={onPanelBlur}
                        ref={(el) => focusPanelRefs.current[jobViewModel.uniqueId] = el}
                    >
                        {jobViewModel.title}
                    </button>
                </h2>
                <div className="p-job_detail">
                    <ul className="p-job_summary">
                        {jobViewModel.isDisplayCompanyName &&
                            <li className="p-job_companyName">
                                <img src={mix("webfront/images/icon/company_gray.svg")} className="p-job_summary_img" width="11" height="17" alt={translationsJobItems.company} />
                                {jobViewModel.companyName}
                            </li>
                        }
                        {jobViewModel.isDisplayLocation &&
                            <li className="p-job_location s-more_list_wrap">
                                <img src={mix("webfront/images/icon/location_gray.svg")} className="p-job_summary_img" width="11" height="17" alt={translationsJobItems.location} />
                                <div>
                                    {jobViewModel?.locations?.map((location, index) => (
                                        <span key={index} className="c-more_list s-more_list">{location}</span>
                                    ))}
                                    {"\u00A0"}
                                    <p className="p-job_location_more_text s-more_text">+ <span className="s-more_num"></span> <span className="s-more_loc">{translationsJobItems.other_locations}</span></p>
                                </div>
                            </li>
                        }
                        {jobViewModel.isDisplayStation &&
                            <li className="p-job_station">
                                <img src={mix("webfront/images/icon/station_gray.svg")} className="p-job_summary_img" width="11" height="17" alt={translationsJobItems.station} />
                                <div>
                                    {jobViewModel.stationName}
                                    <JobTrains trainList={jobViewModel.trainList} />
                                </div>
                            </li>
                        }
                        {jobViewModel.isDisplayEmphasisFeatures &&
                            <li className="p-job_emphasis">
                                {jobViewModel.featureViewModel.remoteFeatures.map((remoteFeature, index) => {
                                    return (
                                        <div key={index} className="p-job_emphasis_item p-job_remote">{remoteFeature}</div>
                                    );
                                })}
                                {jobViewModel.featureViewModel.bonusFeatures.map((bonusFeature, index) => {
                                    return (
                                        <div key={index} className="p-job_emphasis_item p-job_bonus">{bonusFeature}</div>
                                    );
                                })}
                            </li>
                        }
                    </ul>
                    {jobViewModel.isDisplaySalary &&
                        <div className="p-job_salary">{jobViewModel.salary}<span className="p-job_salary_per">{jobViewModel.salaryType}</span></div>
                    }
                    <div className="p-job_description" dangerouslySetInnerHTML={{__html: jobViewModel.snippet}}></div>
                    <div className="p-job_tag_wrap">
                        <KeywordMatchFeatures
                            featureViewModel={jobViewModel.featureViewModel}
                            translationsJobItems={translationsJobItems}
                        />
                        {jobViewModel.isDisplayJobType &&
                            <JobType jobType={jobViewModel.jobType} jobTypeLabel={translationsJobItems.job_type} />
                        }
                        <ImmediatelyHiringFeatures featureViewModel={jobViewModel.featureViewModel} />
                        <PriorityFeatures featureViewModel={jobViewModel.featureViewModel} />
                        <NormalFeatures featureViewModel={jobViewModel.featureViewModel} />
                    </div>
                </div>
            </div>
            <div className="p-job_bottom">
                {jobViewModel.isNew &&
                    <span className="p-job_new">{translations.new}</span>
                }
                <p className="p-job_date">{jobViewModel.formattedUpdateDate}</p>

                <span
                    className="s-job--saved s-biggerLink_except"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSaveJob(e, jobViewModel);
                    }}
                    onKeyPress={e => e.stopPropagation()}
                >
                    <button
                        className={`p-job_saved s-job_saved ${isSaved ? 'is-saved' : ''}`}
                        aria-label={translations.save_job}
                    >
                        <img src={mix("webfront/images/icon/favorite_line_thin.svg")} className="p-job_saved_icon p-job_saved_icon--unsaved" width="16" height="21" alt="" />
                        <img src={mix("webfront/images/icon/favorite.svg")} className="p-job_saved_icon p-job_saved_icon--saved" width="16" height="21" alt="" />
                    </button>
                </span>
            </div>
        </li>
    );
}

/**
 * @param {Array|Object} trainList
 * @example trainList: {21: {id: 21, name: "Line N"}, 22: {id: 22, name: "Line M"}}
 * @example trainList: []
 */
const JobTrains = memo(({trainList}) => {
    // trainListが空なら何も表示しない
    if (Object.keys(trainList).length === 0) {
        return null;
    }

    return (
        <span className="p-job_summary_train">
            {Object.values(trainList).map((train) => {
                return (
                    <img
                        src={mix(`webfront/images/train/train_${train.id}.svg`)}
                        width="17"
                        height="17"
                        alt={train.name}
                        key={train.id}
                    />
                );
            })}
        </span>
    );
});

const JobType = memo(({jobType, jobTypeLabel}) => {
    return (
        <p className="p-job_tag">
            <img
                src={mix("webfront/images/icon/jobtype_bluegray.svg")}
                className="p-job_tag_image"
                width="12"
                height="12"
                alt={jobTypeLabel}
            />
            {jobType}
        </p>
    );
});

const KeywordMatchFeatures = memo(({featureViewModel, translationsJobItems}) => {
    if (featureViewModel.keywordMatchFeatures.length === 0) {
        return null;
    }

    return featureViewModel.keywordMatchFeatures.map((keywordMatchFeature, index) => {
        return (
            <p className="p-job_tag p-job_tag_match" key={index}>
                <img
                    src={mix("webfront/images/icon/check_circle.svg")}
                    className="p-job_tag_image"
                    width="14"
                    height="14"
                    alt={translationsJobItems.matching}
                />
                {keywordMatchFeature}
            </p>
        );
    });
});

const ImmediatelyHiringFeatures = memo(({featureViewModel}) => {
    if (featureViewModel.immediatelyHiringFeatures.length === 0) {
        return null;
    }

    return featureViewModel.immediatelyHiringFeatures.map((immediatelyHiringFeature, index) => {
        return (
            <p className="p-job_tag" key={index}>
                <img src={mix("webfront/images/icon/thunder.svg")} className="p-job_tag_image" width="9" height="15" alt=""/>
                {immediatelyHiringFeature}
            </p>
        );
    });
});

const PriorityFeatures = memo(({featureViewModel}) => {
    if (featureViewModel.priorityOneFeatures.length === 0) {
        return null;
    }

    return featureViewModel.priorityOneFeatures.map((priorityFeature, index) => {
        return (
            <p className="p-job_tag" key={index}>{priorityFeature}</p>
        );
    });
});

const NormalFeatures = memo(({featureViewModel}) => {
    if (featureViewModel.normalFeatures.length === 0) {
        return null;
    }

    return featureViewModel.normalFeatures.map((normalFeature, index) => {
        return (
            <p className="p-job_tag" key={index}>{normalFeature}</p>
        );
    });
});

export default JobPanel;
